import apiClient, { parseTotalCount } from './apiClient';
import {
  IUserProfile,
  IMFAProvisioningUrl,
  IUserProfileUpdate,
  IFinancial,
  IPatientCreate,
  IPatientUpdate,
  IPatient,
  IMinimalPatient,
  IPrescription,
  IPrescriptionWithoutPatient,
  IPrescriptionUpdate,
  IPrescriptionCreate,
  IAppointment,
  IAppointmentCreate,
  IAppointmentUpdate,
  IAppointmentInvoicesAndPayments,
  IAppointmentHonorariumReport,
  IPatientReportRow,
  IGoogleEvent,
  IGoogleEventUpdate,
  IGoogle,
  IEHealthSts,
  ITable,
  IThirdPartyPaymentCreate,
  ISplitProposal,
  INonConventionalHonorarium,
  INonConventionalHonorariumUpdate,
  INonConventionalHonorariumCreate,
  ISelectedNonConventionalTariff,
  IDocument,
  IUnconfirmedAppointment,
  IPlanningConfiguration,
  IAppointmentPaymentSubmit,
  IInvoiceCreate,
  IInvoice,
  IUserInvoiceInformationUpdate,
  IAppointmentHonorarium,
  IInstitution,
  IRequisition,
  IBankAccount,
  IDocumentSize,
  IContactModel,
  IMoveOwner,
  ICount,
  IAlmostExpiredAgreements,
  IInputModelSecondSeries,
  IInputModelReasoningSecondSession,
  IInputModelWrittenReport,
  IInputModelConsultativeResearch,
  IInputModelPathologyOverview,
  IDocumentTemplate,
  ITaskId,
  IEHBoxLocalMessagePayload,
  IEHBoxSentMessageAcks,
  ICodingListResponse,
  ICPCCodingListResponse,
  ICD11CodingListResponse,
} from './interfaces';
import {
  IPatientComment,
  IPatientCommentCreate,
  IPatientCommentUpdate,
} from './features/patients/patient-comments/types';
import {
  IPatientModel,
  IPhysioModel,
} from './interfaces/document_templates';
import {
  IAttestationCreate, IAttestationWithAppointments, IAttestation
} from './store/attestations/interfaces';
import {
  IInvoiceCustomerCreate, IInvoiceCustomer
} from './interfaces/invoice_customer';
import {
  IEAgreement,
  IAskAgreementInput, ICancelAgreementInput
} from './interfaces/eagreement';
import {
  ISendEAttestInput
} from './interfaces/eattest';
import {
  ISendEFactInput, IEfact, IEFactRefusal, IEfactFilter, IEFactInvoiceNumber, ICreditNoteInput
} from './interfaces/efact';
import {
  IPrescriptionFilter
} from './interfaces/prescriptions';
import {
  IInvoiceFilter
} from './interfaces/invoices';
import {
  IBankAccountData, IBankAccountDataFilter
} from './interfaces/bankaccountdata';
import {
  IJournalEntry, IJournalEntryFull, IJournalEntryWrite, IJournalEntryUpdate, IJournalEntriesQuery
} from './interfaces/journal';

export const api = {
  async logInGetToken(username: string, password: string, otp: string | null) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('otp', otp ?? '');
    return apiClient.post('login/access-token', params);
  },
  async refreshToken(refreshToken: string) {
    return apiClient.post('login/refresh-token', { refresh_token: refreshToken });
  },
  async logout(refreshToken: string) {
    return apiClient.post('login/logout', { refresh_token: refreshToken });
  },
  async getOTPRequired(email: string) {
    return apiClient.get(`login/otp-required/${email}`);
  },
  async checkMaintenanceMode() {
    return apiClient.get<{ maintenance: boolean }>('maintenance');
  },
  async getTokenOnBehalfOf(onBehalfOf: string) {
    return apiClient.get(`users/on_behalf_of/${onBehalfOf}`);
  },
  async getMe(checkGoogle = false) {
    if (checkGoogle) {
      return apiClient.get<IUserProfile>('users/me?check_google=true');
    }

    return apiClient.get<IUserProfile>('users/me');
  },
  async getMfaProvisioningUrl() {
    return apiClient.get<IMFAProvisioningUrl>('users/me/mfa');
  },
  async getPhysiotherapyUsers() {
    return apiClient.get<IUserProfile[]>('users/me/physiotherapy');
  },
  async updateMe(data: IUserProfileUpdate) {
    return apiClient.put<IUserProfile>('users/me', data);
  },
  async getUserGoogle(userId: number) {
    return apiClient.get<IGoogle | null>(`users/${userId}/google`);
  },
  /**
   * With `clientPublicKey` (base64 X25519), the passphrase is returned encrypted to that
   * one-time key, together with `server_public_key` and `nonce`.
   */
  async getUserEhealthSts(userId: number, clientPublicKey?: string) {
    return apiClient.get<IEHealthSts | null>(`users/${userId}/ehealth-sts`, {
      params: clientPublicKey ? { client_public_key: clientPublicKey } : undefined,
    });
  },
  async getInvoiceInformation() {
    return apiClient.get('invoice-information/');
  },
  async uploadUserLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('invoice-information/logo', formData);
  },
  async clearUserLogo() {
    return apiClient.delete('invoice-information/logo');
  },
  async updateInvoiceInformation(data: IUserInvoiceInformationUpdate) {
    return apiClient.put('invoice-information/', data);
  },
  async passwordRecovery(email: string) {
    return apiClient.post(`password-recovery/${email}`);
  },
  async resetPassword(password: string, token: string) {
    return apiClient.post('reset-password/', {
      new_password: password,
      token,
    });
  },
  // Google
  async getCalendars() {
    return apiClient.get<string[]>('google/calendar');
  },
  async putCalendar(calendar_id: string) {
    return apiClient.patch('google/', { calendar_id });
  },
  async syncEvents(performSync: boolean, start_date: string | null, end_date: string | null) {
    return apiClient.get<IGoogleEvent[]>('google/appointments', { params: { perform_sync: performSync, start_date, end_date } });
  },
  async updateEvent(eventId: number, data: IGoogleEventUpdate) {
    return apiClient.patch(`google/${eventId}/ignore`, data);
  },
  async setGoogleAuthorizationToken(code: string) {
    return apiClient.post('google/token', { code });
  },
  async deleteGoogle() {
    return apiClient.delete('google/');
  },

  // Nomenclatures
  async getNonConventionalHonoraria() {
    return apiClient.get<INonConventionalHonorarium[]>('non_conventional_honorarium/');
  },
  async updateNonConventionalHonorarium(nonConventionalHonorariumId: number, data: INonConventionalHonorariumUpdate) {
    return apiClient.put(`non_conventional_honorarium/${nonConventionalHonorariumId}`, data);
  },
  async deleteNonConventionalHonorarium(nonConventionalHonorariumId: number) {
    return apiClient.delete(`non_conventional_honorarium/${nonConventionalHonorariumId}`);
  },
  async createNonConventionalHonorarium(data: INonConventionalHonorariumCreate) {
    return apiClient.post('non_conventional_honorarium/', data);
  },
  async getAvailableNonConventionalHonoraria(params: { appointment_start: string; duration?: number | null; location?: string | null; nomenclature_id?: number | null }) {
    return apiClient.get<ISelectedNonConventionalTariff[]>('non_conventional_honorarium/available_honorariums', { params });
  },
  // Care team
  async getCareTeam(patientId: number) {
    return apiClient.get('care_team/', { params: { patient_id: patientId } });
  },
  async initCareTeam(patientId: number) {
    return apiClient.get(`care_team/${patientId}/init`);
  },
  async getCareTeamRecord(id: number) {
    return apiClient.get(`care_team/${id}`);
  },
  async createCareTeamRecord(patientId: number, data: any) {
    return apiClient.post(`care_team/?patient_id=${patientId}`, data);
  },
  async updateCareTeamRecord(id: number, data: any) {
    return apiClient.put(`care_team/${id}`, data);
  },
  async deleteCareTeamRecord(id: number) {
    return apiClient.delete(`care_team/${id}`);
  },
  // Patients
  async countPatients(search_text: string | null = null) {
    return apiClient.get<ICount>('patients/count', { params: { search_text } });
  },
  async getMinimalPatients(skip = 0, limit = 100, search_text: string | null = '', sort_by = 'last_name', sort_order: 'asc' | 'desc' = 'desc', exclude_disabled = false) {
    return apiClient.get<IMinimalPatient[]>('patients/minimal/', { params: { exclude_disabled, skip, limit, search_text, sort_by, sort_order } });
  },
  async countMinimalPatients(search_text: string | null = null) {
    return apiClient.get<ICount>('patients/minimal/count', { params: { search_text } });
  },
  async getPatient(patientId: number) {
    return apiClient.get<IPatient>(`patients/${patientId}`);
  },
  async getPatientBySsin(ssin: string) {
    return apiClient.get<IPatient>(`patients/ssin/${ssin}`);
  },
  async updatePatient(patientId: number, data: IPatientUpdate) {
    return apiClient.put(`patients/${patientId}`, data);
  },
  async disablePatient(patientId: number) {
    // doesn't actually delete, just disable + hides it.
    return apiClient.delete(`patients/${patientId}`);
  },
  async enablePatient(patientId: number) {
    // re-activate.
    return apiClient.get(`patients/${patientId}/enable`);
  },
  async createPatient(data: IPatientCreate) {
    return apiClient.post('patients/', data);
  },
  async mergePatients(sourcePatientId: number, targetPatientId: number) {
    return apiClient.post('patients/merge', {
      source_patient_id: sourcePatientId,
      target_patient_id: targetPatientId,
    });
  },

  async countEPathology(patientId: number) {
    return apiClient.get<number>(`patients/${patientId}/epathology/count`);
  },
  async countFaPathology(patientId: number) {
    return apiClient.get<number>(`patients/${patientId}/fapathology/count`);
  },
  async countFbPathology(patientId: number) {
    return apiClient.get<number>(`patients/${patientId}/fbpathology/count`);
  },

  // Coding lists
  async getCodingList(path: string) {
    return apiClient.get<ICodingListResponse>(path);
  },
  async getIcpcList(q: string, skip = 0, limit = 50) {
    return apiClient.get<ICPCCodingListResponse>('coding-lists/icpc', { params: { q, skip, limit } });
  },
  async getIcd11List(q: string, skip = 0, limit = 50) {
    return apiClient.get<ICD11CodingListResponse>('coding-lists/icd-11', { params: { q, skip, limit } });
  },
  async getAddressTypesCodingList() {
    return apiClient.get<ICodingListResponse>('coding-lists/address-type');
  },
  async getAutonomyCodingList() {
    return apiClient.get<ICodingListResponse>('coding-lists/autonomy');
  },
  async getCivilStateCodingList() {
    return apiClient.get<ICodingListResponse>('coding-lists/civil-state');
  },
  async getContactPersonTypesCodingList() {
    return apiClient.get<ICodingListResponse>('coding-lists/contact-person');
  },
  async getCountryCodesCodingList() {
    return apiClient.get<ICodingListResponse>('coding-lists/country-codes');
  },
  async getEmploymentSituationCodingList() {
    return apiClient.get<ICodingListResponse>('coding-lists/employment-situation');
  },

  async addDocumentToPatient(patientId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file)
    return apiClient.post(`patients/${patientId}/document`, formData);
  },
  async getDocumentsForPatient(patientId: number) {
    return apiClient.get<IDocument[]>(`patients/${patientId}/documents`);
  },

  // Patient comments
  async getPatientComments(patientId?: number) {
    return apiClient.get<IPatientComment[]>('patient_comments/', { params: { patient_id: patientId } });
  },
  async updatePatientComment(patientId: number, data: IPatientCommentUpdate) {
    return apiClient.put(`patient_comments/${patientId}`, data);
  },
  async deletePatientComment(patientId: number) {
    return apiClient.delete(`patient_comments/${patientId}`);
  },
  async createPatientComment(data: IPatientCommentCreate) {
    return apiClient.post('patient_comments/', data);
  },
  async getPatientCommentsReport(prescriptionId: number) {
    return apiClient.get<IPatientComment[]>('patient_comments/report', { params: { prescription_id: prescriptionId } });
  },
  async createPrescriptionCommentsReport(
    prescriptionId: number,
    body: { patient: IPatientModel; physio: IPhysioModel; patient_comment_ids?: number[] },
    cdLinkType = 'isattestationof',
    exportFormat: 'pdf' | 'docx' = 'pdf',
    locale?: string,
  ) {
    return apiClient.post<IDocument>(
      'document_templates/prescription-comments-report',
      body,
      { params: { prescription_id: prescriptionId, cd_link_type: cdLinkType, export_format: exportFormat, locale } },
    );
  },
  // Prescriptions
  async getPrescriptions(filter: IPrescriptionFilter) {
    return apiClient.get<IPrescriptionWithoutPatient[]>('prescriptions/', { params: { skip: filter.skip, limit: filter.limit, search_text: filter.search_text, exclude_disabled_prescriptions: filter.exclude_disabled_prescriptions, exclude_disabled_patients: filter.exclude_disabled_patients, sort_by: filter.sort_by, sort_order: filter.sort_order, no_more_remaining_sessions: filter.no_more_remaining_sessions, patient_id: filter.patient_id} });
  },
  async countPrescriptions(search_text: string | null = null, exclude_disabled_prescriptions = false, exclude_disabled_patients = true, patient_id: number | null = null) {
    return apiClient.get<ICount>('prescriptions/count', { params: { search_text, exclude_disabled_prescriptions, exclude_disabled_patients, patient_id } });
  },
  async getPrescription(prescriptionId: number, incl_disabled_patients = false) {
    return apiClient.get<IPrescription>(`prescriptions/${prescriptionId}?incl_disabled_patients=${incl_disabled_patients}`);
  },
  async updatePrescription(patientId: number, data: IPrescriptionUpdate) {
    return apiClient.put(`prescriptions/${patientId}`, data);
  },
  async deletePrescription(patientId: number) {
    return apiClient.delete(`prescriptions/${patientId}`);
  },
  async createPrescription(data: IPrescriptionCreate) {
    return apiClient.post('prescriptions/', data);
  },
  async addDocumentToPrescription(prescriptionId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file)
    return apiClient.post(`prescriptions/${prescriptionId}/document`, formData);
  },
  async getDocumentsForPrescription(prescriptionId: number) {
    return apiClient.get<IDocument[]>(`prescriptions/${prescriptionId}/documents`);
  },
  // Journal entries (persisted backend copy of sophia-desktop journal entries)
  async getJournalEntries(query: IJournalEntriesQuery = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach((item) => params.append(key, String(item)));
      } else {
        params.append(key, String(value));
      }
    });
    return apiClient.get<IJournalEntry[]>('journal/entries', { params });
  },
  async getJournalEntry(id: number) {
    return apiClient.get<IJournalEntryFull>(`journal/entries/${id}`);
  },
  async getJournalEntryByTransactionId(transactionId: string) {
    return apiClient.get<IJournalEntryFull>(`journal/entries/by-transaction-id/${encodeURIComponent(transactionId)}`);
  },
  async createJournalEntry(data: IJournalEntryWrite) {
    return apiClient.post<IJournalEntryFull>('journal/entries', data);
  },
  async updateJournalEntry(id: number, data: IJournalEntryUpdate) {
    return apiClient.patch<IJournalEntryFull>(`journal/entries/${id}`, data);
  },
  // Attestations
  async getAttestations(skip = 0, limit = 25, search_text: string | null = '', sort_by = 'date', sort_order: 'asc' | 'desc' = 'desc', patient_id: number | null = null) {
    return apiClient.get<IAttestationWithAppointments[]>('attestations/', { params: { skip, limit, search_text, sort_by, sort_order, patient_id } });
  },
  async getAttestationsBasic(skip = 0, limit = 25, search_text: string | null = '', sort_by = 'date', sort_order: 'asc' | 'desc' = 'desc', patient_id: number | null = null) {
    return apiClient.get<IAttestation[]>('attestations/basic/', { params: { skip, limit, search_text, sort_by, sort_order, patient_id } });
  },
  async countAttestations(search_text: string | null = null) {
    return apiClient.get<ICount>('attestations/count', { params: { search_text } });
  },
  async getAttestation(attestationId: number) {
    return apiClient.get<IAttestationWithAppointments>(`attestations/${attestationId}`);
  },
  async createAttestation(data: IAttestationCreate) {
    return apiClient.post<IAttestationWithAppointments>('attestations/', data);
  },
  async updateAttestation(attestationId: number, data: IAttestationCreate) {
    return apiClient.put<IAttestationWithAppointments>(`attestations/${attestationId}`, data);
  },
  async deleteAttestation(attestationId: number) {
    return apiClient.delete(`attestations/${attestationId}`);
  },
  async getPatientAttestationStates(patient_id: number, start: string, end: string) {
    return apiClient.get<{ appointment_id: number; state: 'none' | 'eattest' | 'efact' | 'paper' }[]>(
      `patients/${patient_id}/attestation-states`,
      { params: { start, end } }
    );
  },
  async linkAppointmentToAttestation(attestationId: number, appointmentId: number) {
    return apiClient.post<IAttestationWithAppointments>(`attestations/${attestationId}/appointment/${appointmentId}`, {});
  },
  async unlinkAppointmentToAttestation(attestationId: number, appointmentId: number) {
    return apiClient.delete<IAttestationWithAppointments>(`attestations/${attestationId}/appointment/${appointmentId}`);
  },
  async addDocumentToAttestation(attestationId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file)
    return apiClient.post(`attestations/${attestationId}/document`, formData);
  },
  async getDocumentsForAttestation(attestationId: number) {
    return apiClient.get<IDocument[]>(`attestations/${attestationId}/documents`);
  },
  async emailAttest(id: number) {
    return apiClient.post(`attestations/${id}/send`, {});
  },
  // Appointments
  async getAppointments(start: string | null, end: string | null, patient_id: number | null, only_owned = false) {
    if (start) {
      return apiClient.get<IAppointment[]>('appointments/', { params: { start, end, only_owned, patient_id } });
    }

    if(patient_id) {
      return apiClient.get<IAppointment[]>('appointments/', { params: { patient_id, only_owned } });
    }

    return apiClient.get<IAppointment[]>('appointments/planner/');
  },
  async getAppointmentsWithSchriftelijkVerslag(patient_id: number) {
    return apiClient.get<IAppointment[]>(`patients/${patient_id}/schriftelijk_verslag`);
  },
  async getAppointmentsWithIntake(patient_id: number, prescription_id: number) {
    return apiClient.get<IAppointment[]>(`patients/${patient_id}/intake`, { params: { prescription_id } });
  },
  async getAppointmentHonorarium(id: number) {
    return apiClient.get<IAppointmentHonorarium>(`appointments/${id}/honorarium`);
  },
  async getAppointment(id: number) {
    return apiClient.get<IAppointment>(`appointments/${id}`);
  },
  async getAppointmentEagreements(id: number) {
    return apiClient.get<IEAgreement[]>(`appointments/${id}/eagreements`);
  },
  async getAppointmentsForPatientWithoutAttestation(patient_id: number) {
    return apiClient.get<IAppointment[]>('appointments/', { params: { patient_id, without_attestation: true } });
  },
  async getInvoiceablePatients(): Promise<IMinimalPatient[]> {
    const limit = 100;
    const firstResponse = await apiClient.get<IMinimalPatient[]>('appointments/invoiceable/patients', {
      params: { skip: 0, limit },
    });
    const total = parseTotalCount(firstResponse);
    const results: IMinimalPatient[] = [...firstResponse.data];
    const remaining = Math.ceil((total - limit) / limit);
    await Promise.all(
      Array.from({ length: remaining }, (_, i) =>
        apiClient.get<IMinimalPatient[]>('appointments/invoiceable/patients', {
          params: { skip: (i + 1) * limit, limit },
        }).then(r => results.push(...r.data))
      )
    );
    return results;
  },
  async getInvoiceableAppointmentsForPatient(patient_id: number, part_remgeld = false) {
    return apiClient.get<IAppointment[]>(`appointments/invoiceable/patients/${patient_id}`, { params: { part_remgeld } });
  },
  async getUnconfirmedAppointments() {
    return apiClient.get<IUnconfirmedAppointment[]>('appointments/planner/');
  },
  async getPlannerConfiguration() {
    return apiClient.get<IPlanningConfiguration>('appointments/planner/configuration/');
  },
  async updatePlannerConfiguration(data: IPlanningConfiguration) {
    return apiClient.put('appointments/planner/configuration/', data);
  },
  async checkConflictUnconfirmed(appointment_id: number) {
    return apiClient.get<boolean>(`appointments/planner/${appointment_id}`);
  },
  async confirmUnconfirmedAppointment(appointment_id: number, patient_id: number) {
    return apiClient.post(`appointments/planner/${appointment_id}/${patient_id}`, {});
  },
  async deleteUnconfirmedAppointment(appointment_id: number) {
    return apiClient.delete(`appointments/planner/${appointment_id}`);
  },
  async getAppointmentTable(start: string, end: string) {
    return apiClient.get<ITable[]>('appointments/table', { params: { start, end } });
  },
  async updateAppointment(patientId: number, data: IAppointmentUpdate) {
    return apiClient.put(`appointments/${patientId}`, data);
  },
  async deleteAppointment(patientId: number, forceDelete = false) {
    return apiClient.delete(`appointments/${patientId}`, { params: { force_delete: forceDelete } });
  },
  async createAppointment(data: IAppointmentCreate) {
    return apiClient.post('appointments/', data);
  },
  async createMultiAppointments(data: IAppointmentCreate[]) {
    return apiClient.post('appointments/multi', data);
  },
  async moveAppointment(id: number, data: IMoveOwner) {
    return apiClient.post<IAppointment>(`appointments/${id}/move`, data);
  },
  async getSplitProposal(id: number) {
    return apiClient.get<ISplitProposal[]>(`appointments/${id}/split`);
  },
  async submitSplitProposal(id: number, data: ISplitProposal) {
    return apiClient.post(`appointments/${id}/split`, data);
  },
  async handlePaymentDialog(data: IAppointmentPaymentSubmit) {
    return apiClient.post('appointments/payments', data);
  },
  async getAppointmentsByEFact(efactId: number, excludeInvoiced = false) {
    return apiClient.get<IAppointment[]>(`appointments/efact/${efactId}`, { params: { exclude_invoiced: excludeInvoiced } });
  },
  // Documents
  async createThirdPartyPayment(data: IThirdPartyPaymentCreate) {
    return apiClient.post<Blob>('documents/third_party_payment', data, { responseType: 'blob' });
  },
  async deleteDocument(documentId: number) {
    return apiClient.delete(`documents/${documentId}`);
  },
  async updateDocument(documentId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file)
    return apiClient.put<IDocument>(`documents/${documentId}`, formData);
  },
  async downloadDocument(documentId: number) {
    return apiClient.get<Blob>(`documents/${documentId}/content`, { responseType: 'blob' });
  },
  async getDocument(documentId: number) {
    return apiClient.get<IDocument>(`documents/${documentId}`);
  },
  // Document Templates
  async createSecondSeriesTemplate(data: IInputModelSecondSeries | IInputModelReasoningSecondSession | IInputModelWrittenReport | IInputModelConsultativeResearch | IInputModelPathologyOverview, patientId?: number, prescriptionId?: number, exportFormat: 'pdf' | 'docx' = 'pdf', locale?: string) {
    return apiClient.post<IDocumentTemplate>('document_templates/', data, { params: { patient_id: patientId, prescription_id: prescriptionId, export_format: exportFormat, locale } });
  },
  async getDocumentTemplates(skip = 0, limit = 100) {
    return apiClient.get<IDocumentTemplate[]>('document_templates/', { params: { skip, limit } });
  },
  async getDocumentSize(documentId: string, compress: boolean) {
    return apiClient.get<IDocumentSize>(`documents/${documentId}/size`, { params: { compress } });
  },

  // eHealth
  async registerEHealth(ssin: string, passphrase: string, certificate: File) {
    const formData = new FormData();
    formData.append('ssin', ssin)
    formData.append('passphrase', passphrase)
    formData.append('certificate', certificate)
    return apiClient.post('ehealth/', formData);
  },
  async queryMemberData(ssin: string | null, io: string | null, ioMembership: string | null, notbefore: string, notOnOrAfter: string, hospitalized: boolean, invoicing: boolean) {
    return apiClient.get('ehealth/mda', {
      params: {
        ssin,
        io,
        io_membership: ioMembership,
        notBefore: notbefore,
        notOnOrAfter,
        hospitalized,
        invoicing
      }
    });
  },
  async consultEAgreement(patientId: number, useInsurancymembership: boolean) {
    return apiClient.get<IEAgreement[]>('ehealth/eagreement', { params: { patient_id: patientId, use_insurancy_membership: useInsurancymembership } });
  },
  async consultEAgreementAsync(patientId: number, useInsurancymembership: boolean) {
    return apiClient.post<ITaskId>(`ehealth/eagreement/async?patient_id=${patientId}&use_insurancy_membership=${useInsurancymembership}`);
  },
  async consultEAgreementStatus(taskId: string) {
    return apiClient.get<any>(`ehealth/eagreement/status/${taskId}`);
  },
  async getAgreements(patientId: number) {
    return apiClient.get<IEAgreement[]>(`eagreements/${patientId}`);
  },
  async getAlmostExpiredAgreements() {
    return apiClient.get<IAlmostExpiredAgreements[]>('agreements/almost_expired');
  },
  async getEFacts(filter: IEfactFilter) {
    return apiClient.get<IEfact[]>('ehealth/efacts', { params: { sort_by: filter.sort_by, sort_order: filter.sort_order, skip: filter.skip, limit: filter.limit, start: filter.start, end: filter.end, search_text: filter.search_text } });
  },
  async countEFacts(filter: IEfactFilter) {
    return apiClient.get<ICount>('ehealth/efacts/count', { params: { search_text: filter.search_text, start: filter.start, end: filter.end } });
  },
  async getEFact(id: number) {
    return apiClient.get<IEfact>(`ehealth/efacts/${id}`);
  },
  async getAppointmentEFactRefusals(appointmentId: number) {
    return apiClient.get<IEFactRefusal[]>(`appointments/${appointmentId}/efact-refusals`);
  },
  async getEFactMessages() {
    return apiClient.get('ehealth/efacts/messages');
  },
  async asyncMessages(patientId: number, get_messages = false) {
    return apiClient.get(`ehealth/eagreement/messages/${patientId}`, { params: { get_messages } });
  },
  async askEAgreement(data: IAskAgreementInput) {
    return apiClient.post('ehealth/eagreement/ask', data);
  },
  async cancelEAgreement(data: ICancelAgreementInput) {
    return apiClient.post('ehealth/eagreement/cancel', data);
  },
  async disconnectPrescription(eagreement_id: number) {
    return apiClient.post(`eagreements/${eagreement_id}/disconnect-prescription`);
  },
  async getAddressBook(nihii: string) {
    return apiClient.get(`ehealth/addressbook/${nihii}`);
  },
  async searchAddressBook(last_name_search: string, profession?: string) {
    return apiClient.get(`ehealth/addressbook/search/${last_name_search}`, { params: { 'profession_search':profession } });
  },
  async sendEAttest(data: ISendEAttestInput[], force_retryable = false) {
    return apiClient.post('ehealth/eattest', data, { params: { force_retryable } });
  },
  async retryEAttest(appointment_id: number) {
    return apiClient.post(`ehealth/eattest/retry/${appointment_id}`, {});
  },
  async sendEFact(data: ISendEFactInput) {
    return apiClient.post('ehealth/efact', data);
  },
  async getEFactInvoiceNumber() {
    return apiClient.get<IEFactInvoiceNumber>('ehealth/efact/number');
  },
  async getEFactAppointments(start: string, end: string, only_third_party_payments = false, use_codes = true) {
    return apiClient.get('ehealth/efact/appointments', { params: { start, end, only_third_party_payments, use_codes } });
  },
  async creditEFact(id: number, data: ICreditNoteInput) {
    return apiClient.post<IInvoice>(`ehealth/efacts/${id}/creditnote`, data);
  },
  async cancelEttest(id: number, forceRetryable = false) {
    return apiClient.delete(`ehealth/eattest/${id}`, { params: { force_retryable: forceRetryable } });
  },
  async retryCancelEAttest(id: number) {
    return apiClient.delete(`ehealth/eattest/retry/${id}`);
  },
  // invoices
  async createInvoice(data: IInvoiceCreate, invoiceCustomerId: number | null, locale?: string) {
    return apiClient.post('invoice/', data, { params: { invoice_customer_id: invoiceCustomerId, locale } });
  },
  async deleteInvoice(id: number) {
    return apiClient.delete(`invoice/${id}`);
  },
  async emailInvoice(id: number) {
    return apiClient.post(`invoice/${id}/send`, {});
  },
  async invoicePaid(id: number) {
    return apiClient.post(`invoice/${id}/paid`, {});
  },
  async invoiceUnpaid(id: number) {
    return apiClient.post(`invoice/${id}/unpaid`, {});
  },
  async getInvoices(filter: IInvoiceFilter) {
    return apiClient.get<IInvoice[]>('invoice/', { params: filter });
  },
  async getInvoice(id: number) {
    return apiClient.get<IInvoice>(`invoice/${id}`);
  },
  async countInvoices(search_text: string | null = null, only_owned = false, exclude_paid = false) {
    return apiClient.get<ICount>('invoice/count', { params: { search_text, only_owned, exclude_paid } });
  },
  // financials
  async getFinancials() {
    return apiClient.get<IFinancial[]>('financials/');
  },
  async getUnpaidAppointments(start: string, end: string, search: string | null = null) {
    return apiClient.get<IAppointmentInvoicesAndPayments[]>('financials/appointment_invoices_and_payments', { params: { start, end, search } });
  },
  async getUnattestedAppointments(params: { start: string; end: string; skip?: number; limit?: number; search?: string }) {
    return apiClient.get<IAppointment[]>('financials/unattested_appointments', { params });
  },
  async getAppointmentsWithoutPrescription(params: { start: string; end: string; skip?: number; limit?: number; search?: string }) {
    return apiClient.get<IAppointment[]>('financials/appointments_without_prescription', { params });
  },
  async getPaidAppointments(dateRange: { start: string; end: string } | { paid_start: string; paid_end: string }, user_id?: number) {
    return apiClient.get<IAppointment[]>('financials/paid_appointments', { params: { ...dateRange, ...(user_id !== undefined && { user_id }) } });
  },
  async getPaidInvoices(dateRange: { start: string; end: string } | { paid_start: string; paid_end: string }, user_id?: number) {
    return apiClient.get<IInvoice[]>('financials/paid_invoices', { params: { ...dateRange, ...(user_id !== undefined && { user_id }) } });
  },
  async getAppointmentHonorariumReport(
    start: string,
    end: string,
    user_id?: number,
    extraParams?: {
      search?: string;
      min_age?: number;
      max_age?: number;
      gender?: string;
      include_disabled_patients?: boolean;
      riziv_doctor?: string;
      prescription_pathology?: string;
      location?: string;
      nomenclature_id_numbers?: number[];
    },
  ) {
    const { nomenclature_id_numbers, ...rest } = extraParams ?? {};
    return apiClient.get<IAppointmentHonorariumReport[]>('financials/appointment_honorarium_report', {
      params: {
        start,
        end,
        ...(user_id !== undefined && { user_id }),
        ...rest,
        ...(nomenclature_id_numbers && nomenclature_id_numbers.length > 0 && {
          nomenclature_id_numbers: nomenclature_id_numbers.join(','),
        }),
      },
    });
  },
  async getPatientReport(params: {
    search?: string;
    min_age?: number;
    max_age?: number;
    gender?: string;
    disabled?: boolean;
    has_appointments_start?: string;
    has_appointments_end?: string;
  }) {
    // Remove undefined values so they're not sent as query params
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null),
    );
    return apiClient.get<IPatientReportRow[]>('financials/patient_report', { params: cleanParams });
  },
  // bank account data
  async getInstitutions() {
    return apiClient.get<IInstitution[]>('bank_account_data/institutions');
  },
  async getAccounts() {
    return apiClient.get<IBankAccount[]>('bank_account_data/accounts');
  },
  async checkTransactions() {
    return apiClient.get<IBankAccountData[]>('bank_account_data/account/transactions');
  },
  async processAccountTransaction(transactionId: string, invoiceId: number) {
    return apiClient.post('bank_account_data/account/transactions/process', {
      transaction_id: transactionId,
      invoice_id: invoiceId
    });
  },
  async listTransactions (filter: IBankAccountDataFilter) {
    return apiClient.get<IBankAccountData[]>('bank_account_data/account/transactions/list', { params: filter });
  },
  async countTransactions(filter: IBankAccountDataFilter) {
    return apiClient.get<ICount>('bank_account_data/account/transactions/list/count', { params: filter });
  },
  async postRequisition(institutionId: string) {
    return apiClient.post<IRequisition>('bank_account_data/institutions',
      {
        insitution_id: institutionId,
        redirect_url: `${window.location.href}?goCardless=phase2`
      },
    );
  },
  async postAccount(accountId: string) {
    return apiClient.post<IRequisition>(`bank_account_data/accounts/${accountId}`,
      {},
    );
  },
  async deleteAccount() {
    return apiClient.delete('bank_account_data/accounts');
  },
  async sendContactEmail(data: IContactModel) {
    return apiClient.post('utils/contact-email/', data);
  },
  // Therapeutic Links
  async hasTherapeuticLink(patientId: number) {
    return apiClient.get(`ehealth/therlink/${patientId}`);
  },
  async createTherapeuticLink(patientId: number) {
    return apiClient.post(`ehealth/therlink/${patientId}`, {});
  },
  // Invoice Customers
  async createInvoiceCustomer(data: IInvoiceCustomerCreate) {
    return apiClient.post<IInvoiceCustomer>('invoice_customer/', data);
  },
  async updateInvoiceCustomer(id: number, data: IInvoiceCustomerCreate) {
    return apiClient.put<IInvoiceCustomer>(`invoice_customer/${id}`, data);
  },
  async deleteInvoiceCustomer(id: number) {
    return apiClient.delete<IInvoiceCustomer>(`invoice_customer/${id}`);
  },
  async getInvoiceCustomers(patientId: number) {
    return apiClient.get<IInvoiceCustomer[]>('invoice_customer/', { params: { patient_id: patientId } });
  },
  async getInvoiceCustomer(id: number) {
    return apiClient.get<IInvoiceCustomer>(`invoice_customer/${id}`);
  },
  // EHBox local (backend-stored messages from eHealth Inbox)
  /**
   * Check if a list operation is allowed (e.g. before listing inbox from local eHealth).
   * Returns 201 if allowed; throws with response.status 429 if throttled (list was done in last 20 min).
   */
  async trackEhboxList() {
    return apiClient.get('ehbox-local/tracking/list');
  },
  /**
   * Check if a get (full message) operation is allowed for the given message_id.
   * Returns 201 if allowed; throws with response.status 429 if throttled.
   */
  async trackEhboxGet(messageId?: string) {
    return apiClient.get('ehbox-local/tracking/get', { params: messageId != null ? { message_id: messageId } : {} });
  },
  /**
   * Check if an ack (acknowledgements) operation is allowed for the given message_id.
   * Returns 201 if allowed; throws with response.status 429 if throttled.
   */
  async trackEhboxAck(messageId: string) {
    return apiClient.get('ehbox-local/tracking/ack', { params: { message_id: messageId } });
  },
  async getEhboxLocalMessages(skip = 0, limit = 100, search_text: string | null = null, sort_by = 'received_date', sort_order: 'asc' | 'desc' = 'desc') {
    return apiClient.get('ehbox-local/messages', { params: { skip, limit, search_text, sort_by, sort_order } });
  },
  async countEhboxLocalMessages(search_text: string | null = null) {
    return apiClient.get<ICount>('ehbox-local/messages/count', { params: { search_text } });
  },
  async getEhboxLocalMessageByMessageId(messageId: string) {
    return apiClient.get(`ehbox-local/messages/by-message-id/${encodeURIComponent(messageId)}`);
  },
  // Create a new ehbox-local message (expects full EHBox message JSON body)
  async postEhboxLocalMessage(data: IEHBoxLocalMessagePayload) {
    return apiClient.post('ehbox-local/messages', data);
  },
  async getEHBoxSentMessages(skip = 0, limit = 100, search_text: string | null = null, sort_by = 'sent_date', sort_order: 'asc' | 'desc' = 'desc') {
    return apiClient.get('ehbox-local/sent-messages', { params: { skip, limit, search_text, sort_by, sort_order } });
  },
  async countEHBoxSentMessages(search_text: string | null = null) {
    return apiClient.get<ICount>('ehbox-local/sent-messages/count', { params: { search_text } });
  },
  // EHBox sent-messages (backend-stored SENTBOX messages)
  async getEHBoxSentMessageByMessageId(messageId: string) {
    return apiClient.get(`ehbox-local/sent-messages/by-message-id/${encodeURIComponent(messageId)}`);
  },
  // Create or update a sent EHBox message from the local desktop SENTBOX
  async postEHBoxSentMessage(data: any) {
    return apiClient.post('ehbox-local/sent-messages', data);
  },
  // Set receipt acknowledgments for a sent message (from eHealth acks)
  async postEHBoxSentMessageAck(id: number, data: IEHBoxSentMessageAcks) {
    return apiClient.post(`ehbox-local/sent-messages/${id}/ack`, data);
  },
  async htmlToPdf(html: string, filename: string) {
    return apiClient.post<Blob>('utils/html-to-pdf', { html, filename }, { responseType: 'blob' });
  },
};
